import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { layoutCSS } from "../component/common/commonStyle";

import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import {
  useCreatellmTemplateMutation,
  useGetPhoneNUmberQuery,
} from "../store/services/whatsappApi";

// ─── Shared field styling ──────────────────────────────────────────────────────
// Applied to both TextField and FormControl's sx (for Select).
// Always-visible border → hover darkens → focus purple.
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.9)",
    borderRadius: "8px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#CBD5E1",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94A3B8",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#7c3aed",
      borderWidth: "2px",
    },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E8F0",
    },
    "&.Mui-disabled": {
      background: "rgba(241,245,249,0.6)",
    },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: "#475569" },
  "& .MuiInputBase-input": { fontSize: "0.8rem" },
  "& .MuiSelect-select": { fontSize: "0.8rem" },
  "& .MuiFormHelperText-root": { fontSize: "0.68rem", marginLeft: 0 },
};

// Aliases so existing sx={inputSx} / sx={selectSx} usages keep working
const inputSx = fieldSx;
const selectSx = {};

// ─── Section wrapper ──────────────────────────────────────────────────────────
const SectionPaper = ({ title, children, sx }) => (
  <Paper
    sx={{
      p: 2,
      mb: 2,
      borderRadius: "12px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
      background: "rgba(255,255,255,0.7)",
      ...sx,
    }}
  >
    {title && (
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 600,
          color: "#0F172A",
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Paper>
);

// ─── Helpers ───────────────────────────────────────────────────────────────────
const calculateCounts = (text) => {
  if (!text) return { charCount: 0, smsCount: 0 };
  const charCount = text.length;
  const hasUnicode = /[^\x00-\x7F]/.test(text);
  const maxCharsPerSms = hasUnicode ? 70 : 2000;
  const smsCount = Math.ceil(charCount / maxCharsPerSms);
  return { charCount, smsCount, encoding: hasUnicode ? "Unicode" : "GSM" };
};

const getNextVariableIndex = (texts = []) => {
  let allText = Array.isArray(texts) ? texts.join(" ") : texts || "";
  const matches = allText.match(/\{\{(\d+)\}\}/g);
  if (!matches) return 1;
  const indices = matches.map((m) => parseInt(m.match(/\d+/)[0], 10));
  return Math.max(...indices) + 1;
};

// ─── Default button factory ────────────────────────────────────────────────────
const makeQuickReply = () => ({ type: "quickReply", text: "" });
const makeCallUs = () => ({ type: "callus", buttonText: "", phoneNumber: "" });
const makeCallToAction = () => ({
  type: "callToAction",
  actionType: "Visit Website",
  buttonText: "",
  website: "",
  example: "",
});
const makeOTP = () => ({
  type: "otp",
  otpType: "COPY_CODE",
  buttonText: "Copy Code",
});

const UNSUBSCRIBE_BUTTON = {
  type: "QUICK_REPLY",
  text: "Unsubscribe",
  payload: "UNSUBSCRIBE",
};

const buildAuthenticationPayload = (addSecurityRecommendation, codeExpirationMinutes, buttons) => {
  const components = [];

  components.push({
    type: "BODY",
    add_security_recommendation: !!addSecurityRecommendation,
  });

  const minutes = Math.min(90, Math.max(1, parseInt(codeExpirationMinutes, 10) || 10));
  components.push({
    type: "FOOTER",
    code_expiration_minutes: minutes,
  });

  const authButtons = buttons
    .filter((b) => b.type === "otp")
    .map((b) => ({
      type: "OTP",
      otp_type: b.otpType || "COPY_CODE",
    }));

  if (authButtons.length > 0) {
    components.push({ type: "BUTTONS", buttons: authButtons });
  } else {
    components.push({
      type: "BUTTONS",
      buttons: [{ type: "OTP", otp_type: "COPY_CODE" }],
    });
  }

  return components;
};

// ─── Component ─────────────────────────────────────────────────────────────────
function LlmWhatsappTemplate() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [language, setLanguage] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [hedaerFile, setHeaderFile] = useState(null);
  const [cards, setCards] = useState([]);
  const [activeCardId, setActiveCardId] = useState(null);
  const [nextId, setNextId] = useState(1);
  const [createTemplate] = useCreatellmTemplateMutation();
  const [bodyText, setBodyText] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const { data: phoneData } = useGetPhoneNUmberQuery(customerId, { skip: !customerId });

  // Ph:no is now a multi-select checkbox dropdown — every number is
  // selected by default once phoneData loads (see effect below).
  const [selectedPhnos, setSelectedPhnos] = useState([]);

  const [footerText, setFooterText] = useState("");
  const [headerText, setHeaderText] = useState("");
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(true);
  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState("10");

  // Do Not Change note + static meta submission markers
  const [doNotChange, setDoNotChange] = useState("");
  const [iterationCount, setIterationCount] = useState(0);

  const [buttons, setButtons] = useState([]);

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) { toast.error("Access token not found"); return; }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomerId(id);
    } catch (err) {
      console.error("Error decoding token:", err);
      toast.error("Invalid access token format");
    }
  }, []);

  useEffect(() => {
    if (templateCategory?.toUpperCase() === "AUTHENTICATION") {
      setCategory("TEXT");
      setCards([]);
      setHeaderFile(null);
    }
  }, [templateCategory]);

  // Default: select every available phone number once the list loads.
  // Only auto-fills when nothing has been chosen yet, so it never fights
  // with a user who has already unchecked something.
  useEffect(() => {
    if (phoneData?.phone_numbers?.length && selectedPhnos.length === 0) {
      setSelectedPhnos(phoneData.phone_numbers.map((p) => p.phone_number_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneData]);

  const allPhoneIds = phoneData?.phone_numbers?.map((p) => p.phone_number_id) || [];
  const isAllPhonesSelected =
    allPhoneIds.length > 0 && selectedPhnos.length === allPhoneIds.length;

  const handlePhnoChange = (e) => {
    const value = e.target.value;
    setSelectedPhnos(typeof value === "string" ? value.split(",") : value);
  };

  const handleToggleAllPhones = () => {
    setSelectedPhnos(isAllPhonesSelected ? [] : allPhoneIds);
  };

  // ─── Button helpers ──────────────────────────────────────────────────────────
  const addButton = (type) => {
    if (buttons.length >= 4) return;
    setButtons((prev) => [
      ...prev,
      type === "quickReply" ? makeQuickReply() :
        type === "callToAction" ? makeCallToAction() :
          type === "callus" ? makeCallUs() : null
    ]);
  };

  const removeButton = (index) => {
    setButtons((prev) => prev.filter((_, i) => i !== index));
  };

  const updateButton = (index, field, value) => {
    setButtons((prev) =>
      prev.map((btn, i) => (i === index ? { ...btn, [field]: value } : btn))
    );
  };

  const changeButtonType = (index, newType) => {
    setButtons((prev) =>
      prev.map((btn, i) =>
        i === index
          ? newType === "quickReply"
            ? makeQuickReply()
            : newType === "callus"
              ? makeCallUs()
              : newType === "callToAction"
                ? makeCallToAction()
                : makeOTP()
          : btn
      )
    );
  };

  // ─── Card helpers ────────────────────────────────────────────────────────────
  const addCard = () => {
    const newCard = { id: nextId, smsText: "", file: null, quickReplies: [], urls: [] };
    setCards([...cards, newCard]);
    setActiveCardId(nextId);
    setNextId(nextId + 1);
  };

  const removeCard = (id) => {
    const updated = cards.filter((card) => card.id !== id);
    setCards(updated);
    if (id === activeCardId) setActiveCardId(updated.length ? updated[0].id : null);
  };

  const handleCardChange = (id, field, value) =>
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

  const handleAddCardQuickReply = (cardId) =>
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId ? { ...c, quickReplies: [...(c.quickReplies || []), ""] } : c
      )
    );

  const handleRemoveCardQuickReply = (cardId, idx) =>
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId
          ? { ...c, quickReplies: (c.quickReplies || []).filter((_, i) => i !== idx) }
          : c
      )
    );

  const handleCardQuickReplyChange = (cardId, idx, value) =>
    setCards((prev) =>
      prev.map((c) => {
        if (c.id !== cardId) return c;
        const updated = [...(c.quickReplies || [])];
        updated[idx] = value;
        return { ...c, quickReplies: updated };
      })
    );

  const handleAddCardUrl = (cardId) =>
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId ? { ...c, urls: [...(c.urls || []), { text: "", url: "" }] } : c
      )
    );

  const handleRemoveCardUrl = (cardId, idx) =>
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId
          ? { ...c, urls: (c.urls || []).filter((_, i) => i !== idx) }
          : c
      )
    );

  const handleCardUrlChange = (cardId, idx, field, value) =>
    setCards((prev) =>
      prev.map((c) => {
        if (c.id !== cardId) return c;
        const updated = [...(c.urls || [])];
        updated[idx] = { ...(updated[idx] || {}), [field]: value };
        return { ...c, urls: updated };
      })
    );

  const activeCard = cards.find((c) => c.id === activeCardId);
  const activeCardCounts = activeCard ? calculateCounts(activeCard.smsText) : { charCount: 0 };
  const bodyTextCounts = calculateCounts(bodyText);

  // ─── Variable index helpers ──────────────────────────────────────────────────
  const getBodyNextIndex = () => getNextVariableIndex(bodyText);
  const getHeaderNextIndex = () => getNextVariableIndex(headerText);
  const getFooterNextIndex = () => getNextVariableIndex(footerText);
  const getButtonNextIndex = () => {
    const allTexts = buttons.flatMap((b) =>
      b.type === "quickReply"
        ? [b.text]
        : b.type === "callToAction"
          ? [b.buttonText, b.website]
          : [b.buttonText, b.phoneNumber]
    );
    return getNextVariableIndex(allTexts);
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const formData = new FormData();

      const selectedPhoneObjs = (phoneData?.phone_numbers || []).filter((p) =>
        selectedPhnos.includes(p.phone_number_id)
      );
      const wabaIds = [...new Set(selectedPhoneObjs.map((p) => p.waba_id).filter(Boolean))];

      formData.append("customer_id", customerId || "");
      formData.append("name", templateName || "Default Template Name");
      formData.append("language", language || "en_US");
      formData.append(
        "category",
        (templateCategory?.toUpperCase() || "MARKETING").replace("AUTHORIZATION", "AUTHENTICATION")
      );
      formData.append("template_type", templateCategory?.toUpperCase() === "AUTHENTICATION" ? "TEXT" : category?.toUpperCase());
      formData.append("allow_category_change", "true");

      // Multiple phone numbers can now be selected — send the full set, and
      // keep the singular fields (first selection) for any backend code
      // that still expects one phone_number_id / waba_id.
      formData.append("phone_number_id", selectedPhnos[0] || "");
      formData.append("waba_id", wabaIds[0] || "");
      formData.append("phone_number_ids", JSON.stringify(selectedPhnos));
      formData.append("waba_ids", JSON.stringify(wabaIds));

      formData.append("do_not_change", doNotChange || "");
      formData.append("meta_submit", "true");
      formData.append("iteration_count", String(iterationCount));

      const TYPE = templateCategory?.toUpperCase() === "AUTHENTICATION" ? "TEXT" : category?.toUpperCase();
      let components = [];

      if (templateCategory?.toUpperCase() === "AUTHENTICATION") {
        components = buildAuthenticationPayload(addSecurityRecommendation, codeExpirationMinutes, buttons);
      } else {
        const apiButtons = buttons
          .filter((b) =>
            b.type === "quickReply"
              ? !!b.text
              : b.type === "callToAction"
                ? !!(b.buttonText && b.website)
                : b.type === "callus"
                  ? !!(b.buttonText && b.phoneNumber)
                  : false
          )
          .map((b) => {
            if (b.type === "quickReply") return { type: "QUICK_REPLY", text: b.text };
            if (b.type === "callus") {
              return {
                type: "PHONE_NUMBER",
                text: b.buttonText,
                phone_number: b.phoneNumber,
              };
            }
            const btn = { type: "URL", text: b.buttonText, url: b.website };
            if (b.actionType === "Dynamic Visit") btn.example = [b.example || "example123"];
            return btn;
          });

        apiButtons.push(UNSUBSCRIBE_BUTTON);

        if (TYPE === "TEXT") {
          components.push({ type: "BODY", text: bodyText || "Welcome" });
          if (apiButtons.length > 0) components.push({ type: "BUTTONS", buttons: apiButtons });
        }

        if (TYPE === "MEDIA") {
          let fmt = "IMAGE";
          if (hedaerFile?.type?.startsWith("video")) fmt = "VIDEO";
          if (hedaerFile?.type === "application/pdf") fmt = "DOCUMENT";

          components.push({ type: "HEADER", format: fmt });
          components.push({ type: "BODY", text: bodyText || "" });
          components.push({ type: "FOOTER", text: footerText || "" });
          if (apiButtons.length > 0) components.push({ type: "BUTTONS", buttons: apiButtons });
          formData.append("media", hedaerFile);
        }

        if (TYPE === "INTERACTIVE" && cards.length > 0) {
          components.push({
            type: "CAROUSEL",
            cards: cards.map((c) => {
              const isVideo = c.file?.type?.startsWith("video");
              const temp = [{ type: "HEADER", format: isVideo ? "VIDEO" : "IMAGE" }];
              if (c.smsText) temp.push({ type: "BODY", text: c.smsText });
              const btn = [];
              c.quickReplies?.filter(Boolean).forEach((t) => btn.push({ type: "QUICK_REPLY", text: t }));
              c.urls?.filter((u) => u.text && u.url).forEach((u) =>
                btn.push({ type: "URL", text: u.text, url: u.url })
              );
              if (btn.length > 0) temp.push({ type: "BUTTONS", buttons: btn });
              return { components: temp };
            }),
          });
          if (bodyText) components.push({ type: "BODY", text: bodyText });
          cards.forEach((c) => { if (c.file) formData.append("media", c.file); });
        }
      }

      formData.append("components", JSON.stringify(components));

      await createTemplate(formData).unwrap();
      toast.success("Template Submitted successfully!");
      setTimeout(() => navigate("/whatsapp-list"), 1000);
    } catch (err) {
      console.log("Template Creation Error:", err);
      const errorData = err?.data?.error;
      const apiErrorMessage =
        errorData?.validation_errors?.[0] ||
        errorData?.details ||
        errorData?.error_user_msg ||
        errorData?.message ||
        err?.data?.message ||
        (typeof err?.error === "string" ? err?.error : "Template creation failed");
      toast.error(apiErrorMessage);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ ...layoutCSS, p: 2 }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => window.history.back()} size="small" sx={{ mr: 1 }}>
          <ArrowBackIcon fontSize="medium" />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.05rem" }}>
          Create Template
        </Typography>
      </Box>

      {/* ── Basic Details ──
          Each field is sized to what it actually needs instead of being
          stretched to an even grid column — a short numeric field like
          Iteration Count stays narrow, while Template Name gets more room. */}
      <SectionPaper title="Basic Details">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-start" }}>
          <TextField
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            size="small"
            label="Template Name"
            helperText="Only lowercase letters, numbers, and underscores allowed"
            FormHelperTextProps={{ sx: { mx: 0 } }}
            sx={{ width: 230, ...fieldSx }}
          />

          <FormControl size="small" sx={{ width: 190, ...fieldSx }}>
            <InputLabel id="category-label">Template Category</InputLabel>
            <Select
              labelId="category-label"
              value={templateCategory}
              onChange={(e) => {
                setTemplateCategory(e.target.value);
                setButtons([]);
              }}
              label="Template Category"
            >
              <MenuItem value="MARKETING">Marketing</MenuItem>
              <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
              <MenuItem value="UTILITY">Utility</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: 170, ...fieldSx }}>
            <InputLabel id="template-label">Template Type</InputLabel>
            <Select
              labelId="template-label"
              value={templateCategory?.toUpperCase() === "AUTHENTICATION" ? "TEXT" : category}
              label="Template Type"
              onChange={(e) => setCategory(e.target.value)}
              disabled={templateCategory?.toUpperCase() === "AUTHENTICATION"}
            >
              <MenuItem value="INTERACTIVE">Carousel</MenuItem>
              <MenuItem value="MEDIA">Media</MenuItem>
              <MenuItem value="TEXT">Text</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: 150, ...fieldSx }}>
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              labelId="language-label"
              label="Language"
            >
              <MenuItem value="en_US">English</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Ph:no — checkbox multi-select, every number selected by default */}
          <FormControl size="small" sx={{ width: 220, ...fieldSx }}>
            <InputLabel id="phno-label">Ph:no</InputLabel>
            <Select
              labelId="phno-label"
              label="Ph:no"
              multiple
              value={selectedPhnos}
              onChange={handlePhnoChange}
              renderValue={(selected) =>
                selected.length === 0
                  ? ""
                  : isAllPhonesSelected
                    ? "All numbers"
                    : `${selected.length} selected`
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
            >
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  handleToggleAllPhones();
                }}
                sx={{ py: 0.25 }}
              >
                <Checkbox
                  size="small"
                  checked={isAllPhonesSelected}
                  indeterminate={selectedPhnos.length > 0 && !isAllPhonesSelected}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {phoneData?.phone_numbers?.map((p) => (
                <MenuItem key={p.id} value={p.phone_number_id} sx={{ py: 0.25 }}>
                  <Checkbox size="small" checked={selectedPhnos.indexOf(p.phone_number_id) > -1} />
                  <ListItemText primary={p.phone_number} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Iteration Count — a plain number, so it only gets as much width
              as a 3-4 digit number actually needs. */}
          <TextField
            value={iterationCount}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, "");
              setIterationCount(digits === "" ? 0 : Number(digits));
            }}
            size="small"
            type="number"
            label="Iteration Count"
            inputProps={{ min: 0, style: { MozAppearance: "textfield" } }}
            sx={{ width: 130, ...fieldSx }}
          />
        </Box>
      </SectionPaper>

      {/* ── Content + Preview ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          {/* Header */}
          {templateCategory?.toUpperCase() !== "AUTHENTICATION" && category !== "TEXT" && (
            <SectionPaper title="Header">
              <Typography variant="body2" sx={{ mb: 1.5, color: "#64748B", fontSize: "0.78rem" }}>
                {category === "INTERACTIVE"
                  ? "Add a title to show on the header."
                  : "Add a title, or choose which media file will show on the header."}
              </Typography>

              {category === "MEDIA" && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: "#dc2626", fontSize: "0.78rem" }}>
                    Choose Image, Video, or PDF
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      height: 40,
                      textTransform: "none",
                      borderColor: "#CBD5E1",
                      background: "rgba(255,255,255,0.85)",
                      minWidth: 240,
                      justifyContent: "flex-start",
                      "&:hover": { borderColor: "#94A3B8", background: "#f8fafc" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <ImageIcon fontSize="small" />
                      <VideoLibraryIcon fontSize="small" />
                      <PictureAsPdfIcon fontSize="small" />
                    </Box>
                    <Box component="span" sx={{ fontSize: "0.78rem" }}>
                      Upload Image/Video/PDF
                    </Box>
                    {hedaerFile?.name && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          fontSize: 12,
                          color: "text.secondary",
                          maxWidth: 100,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        | {hedaerFile.name}
                      </Box>
                    )}
                    <input
                      type="file"
                      accept="image/*,video/*,application/pdf"
                      hidden
                      onChange={(e) => setHeaderFile(e.target.files[0])}
                    />
                  </Button>
                </Box>
              )}

              {(category === "INTERACTIVE" || category === "MEDIA") && (
                <TextField
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  fullWidth
                  size="small"
                  label="Header Text (Optional)"
                  sx={fieldSx}
                  helperText={
                    <Button
                      size="small"
                      onClick={() => {
                        setHeaderText((headerText || "") + `{{${getHeaderNextIndex()}}}`);
                      }}
                      sx={{ textTransform: "none", fontSize: "0.7rem", minWidth: 0, p: 0 }}
                    >
                      + Add Variable
                    </Button>
                  }
                />
              )}
            </SectionPaper>
          )}

          {/* Body */}
          <SectionPaper
            title={
              templateCategory?.toUpperCase() === "AUTHENTICATION"
                ? "Authentication Body"
                : "Body"
            }
          >
            {templateCategory?.toUpperCase() === "AUTHENTICATION" && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: "0.78rem" }}>
                  WhatsApp auto-generates the body text ("&lt;VERIFICATION_CODE&gt; is your verification code.") — it can't be customized. You can optionally add a security disclaimer below.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={addSecurityRecommendation}
                      onChange={(e) => setAddSecurityRecommendation(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Add security recommendation disclaimer</Typography>}
                />
              </>
            )}

            {category === "INTERACTIVE" && templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addCard}
                    disabled={cards.length >= 10}
                    startIcon={<AddIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Add Card
                  </Button>
                  <Typography variant="caption" color="error">
                    Must have at least 2 cards
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {cards.map((card) => (
                    <Button
                      key={card.id}
                      variant={card.id === activeCardId ? "contained" : "outlined"}
                      onClick={() => setActiveCardId(card.id)}
                      size="small"
                      sx={{ display: "flex", alignItems: "center", gap: 1, textTransform: "none" }}
                    >
                      <span>Card {card.id}</span>
                      {cards.length > 2 && (
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCard(card.id);
                          }}
                        >
                          ✕
                        </span>
                      )}
                    </Button>
                  ))}
                </Box>

                {activeCard && (
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: "1px solid #E2E8F0",
                      background: "#f8fafc",
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                        Card {activeCard.id}
                      </Typography>

                      <Button
                        variant="outlined"
                        size="small"
                        component="label"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          height: 40,
                          textTransform: "none",
                          borderColor: "#CBD5E1",
                          background: "rgba(255,255,255,0.85)",
                          minWidth: 220,
                          justifyContent: "flex-start",
                          "&:hover": { borderColor: "#94A3B8", background: "#fff" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ImageIcon fontSize="small" />
                          <VideoLibraryIcon fontSize="small" />
                        </Box>
                        <Box component="span" sx={{ fontSize: "0.78rem" }}>
                          Upload Image/Video
                        </Box>
                        {activeCard.file?.name && (
                          <Box
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: 12,
                              color: "text.secondary",
                              maxWidth: 100,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            | {activeCard.file.name}
                          </Box>
                        )}
                        <input
                          type="file"
                          accept="image/*,video/*"
                          hidden
                          onChange={(e) => handleCardChange(activeCard.id, "file", e.target.files[0])}
                        />
                      </Button>

                      <TextField
                        fullWidth
                        placeholder="Enter the text for your message"
                        size="small"
                        multiline
                        value={activeCard.smsText}
                        rows={4}
                        onChange={(e) => handleCardChange(activeCard.id, "smsText", e.target.value)}
                        sx={fieldSx}
                        helperText={
                          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 0.5 }}>
                            <Button
                              size="small"
                              onClick={() => {
                                const nextVar = getNextVariableIndex(activeCard.smsText);
                                handleCardChange(activeCard.id, "smsText", (activeCard.smsText || "") + `{{${nextVar}}}`);
                              }}
                              sx={{ textTransform: "none", fontSize: "0.7rem", minWidth: 0, p: 0 }}
                            >
                              + Add Variable
                            </Button>
                            <Typography sx={{ fontSize: "0.7rem" }}>
                              CharCount: {activeCardCounts.charCount}
                            </Typography>
                          </Box>
                        }
                      />

                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>Card Buttons</Typography>
                        <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                          Each card must have at least one button
                        </Typography>

                        <Typography variant="caption" sx={{ display: "block", color: "#64748B", mt: 1 }}>
                          Quick Replies
                        </Typography>
                        {(activeCard.quickReplies || []).map((qr, idx) => (
                          <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
                            <TextField
                              size="small"
                              value={qr}
                              onChange={(e) => handleCardQuickReplyChange(activeCard.id, idx, e.target.value)}
                              sx={{ flex: 1, ...fieldSx }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCardQuickReply(activeCard.id, idx)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleAddCardQuickReply(activeCard.id)}
                          sx={{ mt: 1, textTransform: "none" }}
                          startIcon={<AddIcon />}
                        >
                          Add Quick Reply
                        </Button>

                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" sx={{ display: "block", color: "#64748B" }}>
                            URL Buttons
                          </Typography>
                          {(activeCard.urls || []).map((u, idx) => (
                            <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
                              <TextField
                                size="small"
                                placeholder="Text"
                                value={u.text}
                                onChange={(e) => handleCardUrlChange(activeCard.id, idx, "text", e.target.value)}
                                sx={{ flex: 1, ...fieldSx }}
                              />
                              <TextField
                                size="small"
                                placeholder="URL"
                                value={u.url}
                                onChange={(e) => handleCardUrlChange(activeCard.id, idx, "url", e.target.value)}
                                sx={{ flex: 1, ...fieldSx }}
                              />
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveCardUrl(activeCard.id, idx)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleAddCardUrl(activeCard.id)}
                            sx={{ mt: 1, textTransform: "none" }}
                            startIcon={<AddIcon />}
                          >
                            Add URL Button
                          </Button>
                        </Box>
                      </Box>
                    </Stack>
                  </Card>
                )}
              </Box>
            )}

            {templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
              <TextField
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={4}
                label="Text write here"
                sx={fieldSx}
                helperText={
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => setBodyText((bodyText || "") + `{{${getBodyNextIndex()}}}`)}
                      sx={{ textTransform: "none", fontSize: "0.7rem", minWidth: 0, p: 0 }}
                    >
                      + Add Variable
                    </Button>
                    <Typography sx={{ fontSize: "0.7rem" }}>
                      CharCount: {bodyTextCounts.charCount}
                    </Typography>
                  </Box>
                }
              />
            )}

            {templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "inline-block",
                    backgroundColor: "#FEF3C7",
                    color: "#92400E",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Highlight
                </Typography>
                <TextField
                  value={doNotChange}
                  onChange={(e) => setDoNotChange(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g. Keep the 50% discount wording unchanged"
                  sx={fieldSx}
                />
              </Box>
            )}
          </SectionPaper>

          {/* Footer */}
          {(templateCategory?.toUpperCase() === "AUTHENTICATION" || category === "MEDIA" ||
            (category === "TEXT" && templateCategory?.toUpperCase() !== "UTILITY")) && (
            <SectionPaper
              title={
                templateCategory?.toUpperCase() === "AUTHENTICATION"
                  ? "Authentication Footer"
                  : "Footer"
              }
            >
              <Typography variant="body2" sx={{ mb: 1.5, color: "#64748B", fontSize: "0.78rem" }}>
                {templateCategory?.toUpperCase() === "AUTHENTICATION"
                  ? "WhatsApp shows a fixed expiration notice — set the number of minutes the code is valid for."
                  : "Add a short line of text to add in the bottom"}
              </Typography>
              {templateCategory?.toUpperCase() === "AUTHENTICATION" ? (
                <TextField
                  value={codeExpirationMinutes}
                  onChange={(e) => setCodeExpirationMinutes(e.target.value.replace(/[^0-9]/g, ""))}
                  fullWidth
                  size="small"
                  type="number"
                  label="Code Expiration (minutes, 1-90)"
                  inputProps={{ min: 1, max: 90 }}
                  sx={fieldSx}
                />
              ) : (
                <TextField
                  value={footerText}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  label="Footer Text"
                  onChange={(e) => setFooterText(e.target.value)}
                  sx={fieldSx}
                  helperText={
                    <Button
                      size="small"
                      onClick={() => setFooterText((footerText || "") + `{{${getFooterNextIndex()}}}`)}
                      sx={{ textTransform: "none", fontSize: "0.7rem", minWidth: 0, p: 0 }}
                    >
                      + Add Variable
                    </Button>
                  }
                />
              )}
            </SectionPaper>
          )}
        </Grid>

        {/* ── Phone Preview ── */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: "sticky", top: 20 }}>
            <Card
              sx={{
                width: 280,
                height: 500,
                margin: "0 auto",
                border: "8px solid #1a1a1a",
                boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                borderRadius: "28px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f8f9fa",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: 30,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#000",
                  color: "#fff",
                }}
              >
                <Typography fontSize="0.7rem" opacity={0.85}></Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WifiIcon sx={{ fontSize: "14px", opacity: 0.85 }} />
                  <NetworkCellIcon sx={{ fontSize: "14px", opacity: 0.85 }} />
                  <BatteryFullIcon sx={{ fontSize: "14px", opacity: 0.85 }} />
                </Stack>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: 48,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #E2E8F0",
                  backgroundColor: "#fff",
                }}
              >
                <IconButton size="small"><ArrowBackIcon fontSize="small" /></IconButton>
                <Typography sx={{ ml: 1, fontWeight: 600, fontSize: "0.85rem" }}>Chat</Typography>
              </Box>

              <Box sx={{ flex: 1, p: 2, backgroundColor: "#e5ddd5", overflowY: "auto" }}>
                {templateCategory?.toUpperCase() !== "AUTHENTICATION" && category === "INTERACTIVE" ? (
                  activeCard?.file && (
                    <Box sx={{ mb: 1 }}>
                      {activeCard.file.type?.startsWith("image") ? (
                        <img
                          src={URL.createObjectURL(activeCard.file)}
                          alt="Card Preview"
                          style={{ maxWidth: "100%", borderRadius: "8px", marginBottom: "6px" }}
                        />
                      ) : (
                        <video
                          src={URL.createObjectURL(activeCard.file)}
                          controls
                          style={{ width: "100%", borderRadius: "8px" }}
                        />
                      )}
                    </Box>
                  )
                ) : templateCategory?.toUpperCase() !== "AUTHENTICATION" && hedaerFile && (
                  <Box sx={{ mb: 1 }}>
                    {hedaerFile.type.startsWith("image") ? (
                      <img
                        src={URL.createObjectURL(hedaerFile)}
                        alt="Header Preview"
                        style={{ maxWidth: "100%", borderRadius: "8px", marginBottom: "6px" }}
                      />
                    ) : hedaerFile.type.startsWith("video") ? (
                      <video
                        src={URL.createObjectURL(hedaerFile)}
                        controls
                        style={{ width: "100%", borderRadius: "8px" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: "#fff",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <PictureAsPdfIcon color="error" />
                        <Typography variant="body2">{hedaerFile.name}</Typography>
                      </Box>
                    )}
                  </Box>
                )}

                <Box
                  component={Paper}
                  sx={{
                    backgroundColor: "white",
                    p: 2,
                    borderRadius: 2,
                    wordWrap: "break-word",
                    boxShadow: "none",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  {category === "INTERACTIVE" && templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
                    <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.5, whiteSpace: "pre-line" }}>
                      {activeCard?.smsText}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.5, whiteSpace: "pre-line" }}>
                    {templateCategory?.toUpperCase() === "AUTHENTICATION"
                      ? `123456 is your verification code.${addSecurityRecommendation ? " For your security, do not share this code." : ""}`
                      : bodyText}
                  </Typography>
                </Box>

                {templateCategory?.toUpperCase() === "AUTHENTICATION" ? (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      sx={{ backgroundColor: "#00a5f4", color: "white", "&:hover": { backgroundColor: "#0088cc" }, textTransform: "none" }}
                    >
                      Copy Code
                    </Button>
                  </Box>
                ) : (
                  category !== "INTERACTIVE" && (
                    <>
                      {buttons
                        .filter((b) => b.type !== "otp")
                        .map((btn, i) => (
                          <Box key={i} sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              fullWidth
                              sx={{
                                backgroundColor: "white",
                                color: "#00a5f4",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#f0f0f0" },
                              }}
                              onClick={() => {
                                if (btn.type === "callToAction" && btn.website) {
                                  window.open(btn.website, "_blank");
                                }
                                if (btn.type === "callus" && btn.phoneNumber) {
                                  window.location.href = `tel:${btn.phoneNumber}`;
                                }
                              }}
                            >
                              {btn.type === "quickReply" ? btn.text : btn.buttonText}
                            </Button>
                          </Box>
                        ))}
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
                        <Button
                          size="small"
                          disableRipple
                          disableElevation
                          sx={{
                            textTransform: "none",
                            color: "#9e9e9e",
                            fontSize: "0.7rem",
                            minWidth: 0,
                            px: 1,
                            py: 0.25,
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#757575",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {UNSUBSCRIBE_BUTTON.text}
                        </Button>
                      </Box>
                    </>
                  )
                )}

                {category === "INTERACTIVE" && activeCard && templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
                  <Box>
                    {activeCard.quickReplies?.filter(Boolean).map((qr, i) => (
                      <Box key={i} sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          sx={{
                            backgroundColor: "white",
                            color: "#00a5f4",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#f0f0f0" },
                          }}
                        >
                          {qr}
                        </Button>
                      </Box>
                    ))}
                    {activeCard.urls?.filter((u) => u?.text).map((u, i) => (
                      <Box key={i} sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          sx={{
                            backgroundColor: "white",
                            color: "#00a5f4",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#f0f0f0" },
                          }}
                          onClick={() => u.url && window.open(u.url, "_blank")}
                        >
                          {u.text}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}

                {templateCategory?.toUpperCase() === "AUTHENTICATION" && (
                  <Box sx={{ mt: 1, p: 1, backgroundColor: "#f0f0f0", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      This code expires in {codeExpirationMinutes || 10} minutes
                    </Typography>
                  </Box>
                )}
                {category === "MEDIA" && footerText && (
                  <Box sx={{ mt: 1, p: 1, backgroundColor: "#f0f0f0", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">{footerText}</Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* ── Buttons Section ── */}
      {templateCategory?.toUpperCase() === "AUTHENTICATION" || category !== "INTERACTIVE" ? (
        <SectionPaper
          title={
            templateCategory?.toUpperCase() === "AUTHENTICATION"
              ? "OTP Buttons"
              : "Create Buttons"
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {templateCategory?.toUpperCase() !== "AUTHENTICATION"
                ? `An "Unsubscribe" quick reply button is always included automatically and doesn't count toward the 4-button limit.`
                : "Add OTP buttons for this authentication template."}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap", ml: 2 }}>
              {buttons.length}/4
            </Typography>
          </Box>

          <Stack spacing={2}>
            {buttons.map((btn, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  backgroundColor: "#f8fafc",
                  borderRadius: 2,
                  border: "1px solid #E2E8F0",
                }}
              >
                {/* Header row: label + delete, so nothing overlaps fields */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    Button {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeButton(index)}
                    sx={{
                      backgroundColor: "rgba(239,68,68,0.08)",
                      "&:hover": { backgroundColor: "rgba(239,68,68,0.18)" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Fields — no absolute positioning, no overlap possible */}
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={3}>
                    <FormControl size="small" fullWidth sx={fieldSx}>
                      <InputLabel>Button Type</InputLabel>
                      <Select
                        value={btn.type}
                        label="Button Type"
                        onChange={(e) => changeButtonType(index, e.target.value)}
                      >
                        {templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
                          <MenuItem value="quickReply">Quick Reply</MenuItem>
                        )}
                        {templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
                          <MenuItem value="callus">Call Us</MenuItem>
                        )}
                        {templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
                          <MenuItem value="callToAction">Call to Action</MenuItem>
                        )}
                        {templateCategory?.toUpperCase() === "AUTHENTICATION" && (
                          <MenuItem value="otp">OTP Button</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  {btn.type === "quickReply" && (
                    <Grid item xs={12} sm={9}>
                      <TextField
                        size="small"
                        label="Button Text"
                        value={btn.text}
                        onChange={(e) => updateButton(index, "text", e.target.value)}
                        sx={fieldSx}
                        fullWidth
                      />
                    </Grid>
                  )}

                  {btn.type === "callToAction" && (
                    <>
                      <Grid item xs={12} sm={3}>
                        <FormControl size="small" fullWidth sx={fieldSx}>
                          <InputLabel>Action Type</InputLabel>
                          <Select
                            value={btn.actionType}
                            label="Action Type"
                            onChange={(e) => updateButton(index, "actionType", e.target.value)}
                          >
                            <MenuItem value="Visit Website">Visit Website</MenuItem>
                            <MenuItem value="Dynamic Visit">Dynamic Visit</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Button Text"
                          value={btn.buttonText}
                          onChange={(e) => updateButton(index, "buttonText", e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Website URL"
                          value={btn.website}
                          onChange={(e) => updateButton(index, "website", e.target.value)}
                          sx={fieldSx}
                          helperText={
                            btn.actionType === "Dynamic Visit" && (
                              <Button
                                size="small"
                                onClick={() => {
                                  updateButton(index, "website", (btn.website || "") + `{{${getButtonNextIndex()}}}`);
                                }}
                                sx={{ textTransform: "none", fontSize: "0.7rem", minWidth: 0, p: 0 }}
                              >
                                + Add Variable
                              </Button>
                            )
                          }
                        />
                      </Grid>
                      {btn.actionType === "Dynamic Visit" && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Example (for variable)"
                            placeholder="e.g. s2BH5vSTTb0"
                            value={btn.example}
                            onChange={(e) => updateButton(index, "example", e.target.value)}
                            sx={fieldSx}
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  {btn.type === "callus" && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Button Text"
                          value={btn.buttonText}
                          onChange={(e) => updateButton(index, "buttonText", e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Phone Number"
                          placeholder="+919876543210"
                          value={btn.phoneNumber}
                          onChange={(e) => updateButton(index, "phoneNumber", e.target.value)}
                          sx={fieldSx}
                          helperText="Include country code"
                        />
                      </Grid>
                    </>
                  )}

                  {btn.type === "otp" && (
                    <>
                      <Grid item xs={12} sm={3}>
                        <FormControl size="small" fullWidth sx={fieldSx}>
                          <InputLabel>OTP Type</InputLabel>
                          <Select
                            value={btn.otpType}
                            label="OTP Type"
                            onChange={(e) => updateButton(index, "otpType", e.target.value)}
                          >
                            <MenuItem value="COPY_CODE">Copy Code</MenuItem>
                            <MenuItem value="ONE_TAP">One Tap</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Button Text"
                          value={btn.buttonText}
                          onChange={(e) => updateButton(index, "buttonText", e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            ))}
          </Stack>

          {buttons.length < 4 && (
            <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
              {templateCategory?.toUpperCase() !== "AUTHENTICATION" && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addButton("quickReply")}
                    sx={{ textTransform: "none" }}
                  >
                    Quick Reply
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addButton("callus")}
                    sx={{ textTransform: "none" }}
                  >
                    Call Us
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addButton("callToAction")}
                    sx={{ textTransform: "none" }}
                  >
                    Call to Action
                  </Button>
                </>
              )}
              {templateCategory?.toUpperCase() === "AUTHENTICATION" && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addButton("otp")}
                  sx={{ textTransform: "none" }}
                >
                  OTP Button
                </Button>
              )}
            </Box>
          )}

          {buttons.length >= 4 && (
            <Typography variant="caption" color="error" sx={{ mt: 1.5, display: "block" }}>
              Maximum 4 buttons reached.
            </Typography>
          )}
        </SectionPaper>
      ) : null}

      {/* ── Submit ── */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3, mb: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          sx={{ minWidth: 120, textTransform: "none" }}
          onClick={() => window.history.back()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ minWidth: 160, textTransform: "none", fontWeight: 600 }}
          onClick={handleSubmit}
        >
          Submit Template
        </Button>
      </Box>
    </Box>
  );
}

export default LlmWhatsappTemplate;